/**
 * The pitch of a recording, frame by frame, for drawing a tone's shape.
 *
 * YIN (de Cheveigné and Kawahara, 2002), the standard estimator for one
 * voice: a frame's difference function, normalised by its running mean, and
 * the first lag under a threshold taken as the period. It runs in the
 * browser on the site's recording and on the learner's own, so the drawing
 * costs nothing per attempt and the learner's voice is read on their device.
 *
 * **It is a picture and never a verdict.** BytePlus and the SNU model decide
 * whether a word was heard as meant. This only draws the voice's rise and
 * fall, smoothed, with the gaps where nothing was voiced left as gaps.
 */

/** One frame of a recording. */
export interface PitchFrame {
  /** Seconds from the start, at the middle of the frame. */
  readonly at: number;

  /** How loud the frame is, as root mean square. */
  readonly rms: number;

  /** The pitch in hertz, or nothing where the frame is not voiced. */
  readonly hz?: number;
}

/** The lowest and highest pitch looked for, in hertz. */
const lowestHz = 70;
const highestHz = 450;

/** A frame every 10 ms, each 40 ms long, which holds two periods at 70 Hz. */
const hopSeconds = 0.01;
const windowSeconds = 0.04;

/** YIN's threshold on the normalised difference. 0.15 is the paper's. */
const threshold = 0.15;

/**
 * The deepest dip taken where none is under {@link threshold}, as the paper's
 * fallback does. The 词典's male recordings are MP3s of a low voice, and their
 * dips sat between 0.3 and 0.5 through a whole fourth tone when measured on
 * 下雨 (2026-10-25), which the strict threshold drew as silence.
 */
const fallbackThreshold = 0.5;

/** A frame quieter than this share of the loudest is taken as silence. */
const quietShare = 0.06;

/** The fewest voiced frames in a row kept, 50 ms, so a click draws nothing. */
const shortestRun = 5;

/** The period of one frame, in samples, or nothing where it is not voiced. */
function periodOf(
  samples: Float32Array,
  start: number,
  size: number,
  shortest: number,
  longest: number,
): number | undefined {
  // Every index read below is in range. `pitchOf` stops a frame
  // `longest + 1` samples short of the end, and both arrays hold every lag up
  // to `longest + 1`, so each `!` is a typed array read inside its length.
  const difference = new Float32Array(longest + 2);

  for (let lag = 1; lag <= longest + 1; lag += 1) {
    let sum = 0;

    for (let at = 0; at < size; at += 1) {
      const delta = samples[start + at]! - samples[start + at + lag]!;

      sum += delta * delta;
    }

    difference[lag] = sum;
  }

  // The cumulative mean normalised difference, which is 1 at no lag.
  let running = 0;
  const normalised = new Float32Array(longest + 2);

  normalised[0] = 1;

  for (let lag = 1; lag <= longest + 1; lag += 1) {
    running += difference[lag]!;
    normalised[lag] = running === 0 ? 1 : (difference[lag]! * lag) / running;
  }

  let deepest = shortest;

  for (let lag = shortest; lag <= longest; lag += 1) {
    if (normalised[lag]! < normalised[deepest]!) {
      deepest = lag;
    }
  }

  const first = Array.from(
    { length: longest - shortest + 1 },
    (_, at) => shortest + at,
  ).find((lag) => normalised[lag]! < threshold);
  const dip =
    first ?? (normalised[deepest]! < fallbackThreshold ? deepest : undefined);

  if (dip === undefined) {
    return undefined;
  }

  // Down to the bottom of this dip.
  let best = dip;

  while (best + 1 <= longest && normalised[best + 1]! < normalised[best]!) {
    best += 1;
  }

  // A parabola through the dip and its neighbours, for a lag between samples.
  // `best` is at least `shortest`, which is at least 1 at any sample rate a
  // browser records at, and at most `longest`.
  const before = normalised[best - 1]!;
  const here = normalised[best]!;
  const after = normalised[best + 1]!;
  const bend = before - 2 * here + after;

  return bend === 0 ? best : best + (before - after) / (2 * bend);
}

/** The middle of some numbers. */
function median(values: readonly number[]): number {
  const sorted = values.toSorted((one, other) => one - other);

  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/**
 * Every frame's pitch, smoothed.
 *
 * A pitch an octave from the recording's median is folded back towards it,
 * since YIN's commonest slip is a period too long or too short by half. Each
 * pitch is then the median of the seven frames around it, and a voiced run
 * shorter than {@link shortestRun} frames is dropped.
 * @param samples - Mono samples, -1 to 1.
 * @param sampleRate - Samples a second.
 * @returns A frame every 10 ms.
 */
export function pitchOf(
  samples: Float32Array,
  sampleRate: number,
): PitchFrame[] {
  const hop = Math.round(hopSeconds * sampleRate);
  const size = Math.round(windowSeconds * sampleRate);
  const shortest = Math.floor(sampleRate / highestHz);
  const longest = Math.ceil(sampleRate / lowestHz);
  const raw: { at: number; rms: number; hz?: number }[] = [];

  for (
    let start = 0;
    start + size + longest + 1 < samples.length;
    start += hop
  ) {
    let energy = 0;

    for (let at = 0; at < size; at += 1) {
      // The loop's own bound keeps `start + at` inside the samples.
      energy += samples[start + at]! ** 2;
    }

    raw.push({
      at: (start + size / 2) / sampleRate,
      rms: Math.sqrt(energy / size),
    });
  }

  const loudest = Math.max(0, ...raw.map((frame) => frame.rms));

  raw.forEach((frame, index) => {
    if (frame.rms < loudest * quietShare) {
      return;
    }

    const period = periodOf(samples, index * hop, size, shortest, longest);

    if (period !== undefined) {
      frame.hz = sampleRate / period;
    }
  });

  const voiced = raw.flatMap((frame) =>
    frame.hz === undefined ? [] : [frame.hz],
  );
  const centre = median(voiced);

  for (const frame of raw) {
    if (frame.hz !== undefined && centre > 0) {
      if (frame.hz > centre * 1.7) {
        frame.hz /= 2;
      } else if (frame.hz < centre / 1.7) {
        frame.hz *= 2;
      }
    }
  }

  const smoothed = raw.map((frame, index) => {
    if (frame.hz === undefined) {
      return { at: frame.at, rms: frame.rms };
    }

    const around = raw
      .slice(Math.max(0, index - 3), index + 4)
      .flatMap((one) => (one.hz === undefined ? [] : [one.hz]));

    return { at: frame.at, rms: frame.rms, hz: median(around) };
  });

  // Drop voiced runs too short to be a syllable.
  let runStart = -1;

  for (let index = 0; index <= smoothed.length; index += 1) {
    const isVoiced = smoothed[index]?.hz !== undefined;

    if (isVoiced && runStart === -1) {
      runStart = index;
    } else if (!isVoiced && runStart !== -1) {
      if (index - runStart < shortestRun) {
        for (let at = runStart; at < index; at += 1) {
          // A run lies between `runStart` and `index`, both inside `smoothed`.
          const frame = smoothed[at]!;

          smoothed[at] = { at: frame.at, rms: frame.rms };
        }
      }

      runStart = -1;
    }
  }

  return smoothed;
}

/**
 * Each voiced frame's pitch in semitones from the speaker's own median.
 *
 * A man's voice and a woman's sit an octave apart, and a tone is a shape
 * within one speaker's range, so the drawing places both on one scale.
 */
export function semitonesOf(
  frames: readonly PitchFrame[],
): (number | undefined)[] {
  const voiced = frames.flatMap((frame) =>
    frame.hz === undefined ? [] : [frame.hz],
  );
  const centre = median(voiced);

  return frames.map((frame) =>
    frame.hz === undefined || centre === 0
      ? undefined
      : 12 * Math.log2(frame.hz / centre),
  );
}
